import { isPromotionActive } from '@/components/biz/promotions/List';
import { BoxChip } from '@/components/biz/promotions/styles';
import LazyLoadedResponsiveImage from '@/components/ui/LazyLoadedResponsiveImage';
import LocalizedLink from '@/components/ui/LocalizedLink';
import WrappedButton from '@/components/ui/WrappedButton';
import { Card, CardContent, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { PersistRatioImage } from '@repo/ui';
import { t } from 'i18next';
import Link from 'next/link';
import router from 'next/router';
import saturdayImg from '@/components/biz/rewards/assets/saturday-free-spin.png';
import depositImg from '@/components/biz/rewards/assets/referral.png';
import thursdayImg from '@/components/biz/rewards/assets/thursday-reload.png';
import { Close as CloseIcon } from '@mui/icons-material';
import { ActivityTypeEnum, IAcvitityItem } from '@/apis/promotions';

import ThursdayReladListItemImg from '/public/assets/images/promotion/v2/thursday-reload-list-item.png';
import SaturdayFreeSpinsListItemImg from '/public/assets/images/promotion/v2/saturday-freespins-list-item.png';
import { useTranslation } from 'next-i18next';
import { formatDate } from '@/utils/time';
import { useSetAtom } from 'jotai';
import { globalModals } from '@/state/globalSettings';
import requestIdleCallback from '@/utils/requestIdleCallback';

export const specialPromotions = [
  {
    title: 'Thursday Reloaded Bonus',
    description: '50% up to 200 USDT+100 Free Spins ',
    slug: 'weekly/thursday-reload',
    cardImage: ThursdayReladListItemImg.src,
    isBonus: true,
    type: ActivityTypeEnum.THURSDAY,
  },
  {
    title: 'Saturday Freespins Bonus',
    description: '50% up to 200 USDT+100 Free Spins ',
    slug: 'weekly/saturday-freespins',
    cardImage: SaturdayFreeSpinsListItemImg.src,
    isBonus: true,
    type: ActivityTypeEnum.SATURDAY,
  },
];

const BonusItem = ({
  activity,
  handleSelectedActivity,
}: {
  activity: IAcvitityItem;
  handleSelectedActivity: any;
}) => {
  const item = specialPromotions.find((i) => i.type === activity.type);
  const bonus = {
    ...item,
    endTime: activity.endTime,
    desc: activity.description,
  };

  return (
    <Card
      component={'div'}
      sx={{
        p: 1,
        backgroundColor: '#191A1F',
        marginBottom: 2,
        cursor: activity.selected ? 'default' : 'pointer',
      }}
      onClick={() => handleSelectedActivity(activity)}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '4px',
          overflow: 'hidden',
          position: 'relative',
          '&:hover .moon-promotion-bonus-list-item': {},
        }}
      >
        <LazyLoadedResponsiveImage
          sources={[{ srcSet: bonus.cardImage, media: '' }]}
          src={bonus.cardImage}
          alt={bonus.title}
        />
      </Box>
      <CardContent sx={{ px: 1, position: 'relative' }}>
        <Typography
          gutterBottom
          color="text.secondary"
          variant="body2"
          fontSize={'12px'}
          fontWeight={400}
          component="div"
        >
          {bonus.endTime
            ? `Finishes on ${formatDate(bonus.endTime)}`
            : 'Permanent'}
        </Typography>
        <Typography
          variant="body2"
          fontSize={'16px'}
          fontWeight={500}
          color="text.primary"
        >
          {bonus.title}
        </Typography>
        <Typography
          variant="body2"
          fontSize={'14px'}
          fontWeight={500}
          color="text.secondary"
        >
          {bonus.description}
        </Typography>

        {isPromotionActive(bonus.endTime) ? (
          <BoxChip
            sx={{
              position: 'absolute',
              right: '0',
              top: '16px',
            }}
            label={t('In Progress')}
          />
        ) : (
          <></>
        )}
      </CardContent>
    </Card>
  );
};

export const WelcomeBonusItem = ({
  activity,
  handleSelectedActivity,
  handleCancelActivity,
}: {
  activity: IAcvitityItem;
  handleSelectedActivity: any;
  handleCancelActivity: any;
}) => {
  const [t] = useTranslation();
  const setModals = useSetAtom(globalModals);
  const item = specialPromotions.find((i) => i.type === activity.type);
  const bonus = {
    ...item,
    ...activity,
  };

  return (
    <Card sx={{ backgroundColor: '#4F4013' }}>
      <CardContent sx={{ padding: '12px !important', cursor: 'pointer' }}>
        <Stack
          direction="row"
          alignItems={'center'}
          onClick={() => handleSelectedActivity(bonus)}
          gap={1}
        >
          <Box sx={{ width: '60px', height: '60px', flex: 'none' }}>
            <PersistRatioImage
              src={
                activity.type === ActivityTypeEnum.WELCOME_BONUS
                  ? depositImg.src
                  : activity.type === ActivityTypeEnum.THURSDAY
                    ? thursdayImg.src
                    : saturdayImg.src
              }
              logicWidth={86}
              logicHeight={82}
              alt={'Welcome Bonus'}
            />
          </Box>
          <Stack
            sx={{ display: 'flex', justifyContent: 'center', marginRight: 1 }}
          >
            <Typography
              variant="body2"
              color="white"
              lineHeight={'15px'}
              fontWeight={600}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              {bonus.title}
            </Typography>
            <Typography variant="body2" color="rgba(255, 255, 255, 0.45)">
              {bonus.description}
            </Typography>
            <span
              style={{
                alignSelf: 'flex-start',
              }}
            >
              <LocalizedLink
                href={
                  activity.type === ActivityTypeEnum.WELCOME_BONUS
                    ? '/benefits/deposit'
                    : `/promotions/weekly/${activity.type === ActivityTypeEnum.SATURDAY ? 'saturday-freespins' : 'thursday-reload'}`
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setTimeout(() => {
                    setModals();
                  }, 500);
                }}
                sx={{ color: 'primary.main' }}
              >
                {t('More')}
              </LocalizedLink>
            </span>
          </Stack>
          {bonus.selected ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Typography
                onClick={(e) => handleCancelActivity(e, bonus)}
                variant="body2"
                color="primary.main"
                lineHeight={'15px'}
                fontWeight={600}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                {t('Cancel')}
              </Typography>
            </Box>
          ) : (
            <></>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default BonusItem;
